// frontend/src/pages/OrderTracking.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  faSearch,
  faTruck,
  faBox,
  faCheckCircle,
  faClock,
  faMapMarkerAlt,
  faCalendarAlt,
  faShippingFast,
  faExclamationTriangle,
  faShip,
  faAnchor,
  faBoxes,
  faUser,
  faEnvelope,
  faPhone,
  faBoxOpen,
  faSeedling,
  faArrowLeft,
  faSpinner,
  faGlobe,
  faWarehouse,
  faBuilding,
  faRocket,
  faInfoCircle,
  faBell,
  faCheckDouble,
  faLocationDot,
  faCircle,
  faEllipsisH,
  faPackage,
  faDollarSign,
  faClipboardCheck,
  faUserCheck,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import { trackShipment, getShipmentByOrder, getUserOrders, getAllShipments } from "../api";
import Loader from "../components/UI/Loader";
import Button from "../components/UI/Button";
import { Link } from "react-router-dom";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// SUPPLIER CONSTANTS
// ============================================
const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    ordered: "#D9A321",
    processing: "#3b82f6",
    packed: "#8b5cf6",
    shipped: "#8b5cf6",
    in_transit: "#f59e0b",
    customs: "#ef4444",
    local_delivery: "#10b981",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    delayed: "#dc2626",
    cancelled: "#6b7280",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusIcon = (status) => {
  const icons = {
    ordered: faClock,
    processing: faBox,
    packed: faBoxOpen,
    shipped: faShippingFast,
    in_transit: faTruck,
    customs: faExclamationTriangle,
    local_delivery: faTruck,
    out_for_delivery: faTruck,
    delivered: faCheckCircle,
    delayed: faExclamationTriangle,
    cancelled: faExclamationTriangle,
  };
  return icons[status] || faTruck;
};

const getStatusLabel = (status) => {
  const labels = {
    ordered: "📋 Order Received",
    processing: "⚙️ Processing in Malaysia",
    packed: "📦 Packed & Ready",
    shipped: "🚢 Shipped from Malaysia",
    in_transit: "🚚 In Transit",
    customs: "🛃 Customs Clearance",
    local_delivery: "🚛 Local Delivery",
    out_for_delivery: "📬 Out for Delivery",
    delivered: "✅ Delivered",
    delayed: "⚠️ Delayed",
    cancelled: "❌ Cancelled",
  };
  return labels[status] || status;
};

const getProgressPercentage = (status) => {
  const steps = {
    ordered: 5,
    processing: 20,
    packed: 35,
    shipped: 50,
    in_transit: 65,
    customs: 75,
    local_delivery: 85,
    out_for_delivery: 95,
    delivered: 100,
    delayed: 50,
    cancelled: 0,
  };
  return steps[status] || 0;
};

const isFromMalaysia = (shipment) => {
  return shipment.origin?.country === "Malaysia" || 
         shipment.supplier?.country === "Malaysia" ||
         shipment.origin?.country === undefined;
};

// ============================================
// MAIN COMPONENT
// ============================================
const OrderTracking = () => {
  const { user } = useAuth();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [shipment, setShipment] = useState(null);
  const [myShipments, setMyShipments] = useState([]);
  const [inventoryShipments, setInventoryShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("track");
  const [isSearching, setIsSearching] = useState(false);
  const [showInventory, setShowInventory] = useState(false);

  // ============================================
  // FETCH SHIPMENTS
  // ============================================
  useEffect(() => {
    if (user) {
      fetchUserShipments();
      fetchInventoryShipments();
    }
  }, [user]);

  const fetchUserShipments = async () => {
    try {
      const orders = await getUserOrders();
      const shipmentsData = [];

      for (const order of orders.data) {
        try {
          const shipmentRes = await getShipmentByOrder(order._id);
          if (shipmentRes.data) {
            shipmentsData.push({
              ...shipmentRes.data,
              orderNumber: order.orderNumber,
            });
          }
        } catch (err) {}
      }
      setMyShipments(shipmentsData);
    } catch (error) {
      console.error("Error fetching user shipments:", error);
    }
  };

  const fetchInventoryShipments = async () => {
    try {
      const response = await getAllShipments({ limit: 50 });
      const shipments = response.data?.shipments || [];
      const inventory = shipments.filter(s => s.orderType === "inventory" && s.isActive !== false);
      setInventoryShipments(inventory);
    } catch (error) {
      console.error("Error fetching inventory shipments:", error);
    }
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;

    setIsSearching(true);
    setLoading(true);
    setError("");
    setShipment(null);

    try {
      const { data } = await trackShipment(trackingNumber);
      setShipment(data);
    } catch (err) {
      setError(err.response?.data?.message || "Tracking number not found");
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const handleViewShipment = (shipmentData) => {
    setShipment(shipmentData);
    setActiveTab("track");
    setTrackingNumber(shipmentData.trackingNumber);
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <motion.div
      className="tracking-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="tracking-hero">
        <div className="tracking-hero__bg" />
        <div className="container tracking-hero__content">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FontAwesomeIcon icon={faTruck} className="tracking-hero__icon" />
            <h1>Track Your Order</h1>
            <p>
              Enter your tracking number to get real-time updates on your
              shipment from Kenali Enterprise. All products are sourced from{" "}
              <strong>{SUPPLIER.name}</strong> in{" "}
              <strong>{SUPPLIER.country}</strong>.
            </p>
            <div className="tracking-hero__info">
              <span>
                <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country}
              </span>
              <span>
                <FontAwesomeIcon icon={faBuilding} /> {SUPPLIER.name}
              </span>
              <span>
                <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
              </span>
            </div>
            <a
              href="https://wa.me/23231030808"
              target="_blank"
              rel="noopener noreferrer"
              className="tracking-hero__whatsapp"
            >
              <FontAwesomeIcon icon={faWhatsapp} />
              Need help? WhatsApp us
            </a>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          MAIN CONTENT
          ============================================ */}
      <div className="container tracking-content">
        <Link to="/" className="tracking-back">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
        </Link>

        {/* ============================================
            INVENTORY SHIPMENT NOTICE
            ============================================ */}
        {inventoryShipments.length > 0 && (
          <motion.div
            className="tracking-inventory-notice"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="tracking-inventory-notice__content">
              <FontAwesomeIcon icon={faShip} />
              <div>
                <strong>New Shipment from {SUPPLIER.name} ({SUPPLIER.country})!</strong>
                <span>
                  {inventoryShipments.length} shipment{inventoryShipments.length > 1 ? "s" : ""} currently in transit.
                  Pre-order customers will be notified when cleared.
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowInventory(!showInventory)}
              className="tracking-inventory-notice__toggle"
            >
              {showInventory ? "Hide Details" : "View Details"}
            </button>
          </motion.div>
        )}

        {/* ============================================
            INVENTORY SHIPMENTS LIST
            ============================================ */}
        <AnimatePresence>
          {showInventory && inventoryShipments.length > 0 && (
            <motion.div
              className="tracking-inventory-list"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <h4>
                <FontAwesomeIcon icon={faShip} /> Incoming Shipments from {SUPPLIER.name}
              </h4>
              {inventoryShipments.map((ship) => (
                <div key={ship._id} className="tracking-inventory-item">
                  <div>
                    <div className="tracking-inventory-item__tracking">
                      {ship.trackingNumber}
                      <span className="tracking-inventory-item__badge">
                        <FontAwesomeIcon icon={faGlobe} /> MY
                      </span>
                    </div>
                    <div className="tracking-inventory-item__route">
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> {ship.origin?.city || SUPPLIER.city} → {ship.destination?.city || "Freetown"}
                    </div>
                  </div>
                  <div className="tracking-inventory-item__status">
                    <span
                      className="tracking-inventory-item__status-badge"
                      style={{ background: getStatusColor(ship.status) }}
                    >
                      {getStatusLabel(ship.status)}
                    </span>
                    <span className="tracking-inventory-item__date">
                      {ship.estimatedDelivery ? `Est: ${new Date(ship.estimatedDelivery).toLocaleDateString()}` : "TBD"}
                    </span>
                  </div>
                </div>
              ))}
              <div className="tracking-inventory-list__note">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  Pre-order customers will receive priority access when shipments are cleared and ready for pickup.
                  Contact us on WhatsApp for updates.
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ============================================
            TRACKING TABS
            ============================================ */}
        <div className="tracking-tabs-wrapper">
          {user && (
            <div className="tracking-tabs">
              <button
                className={`tracking-tab ${activeTab === "track" ? "active" : ""}`}
                onClick={() => setActiveTab("track")}
              >
                <FontAwesomeIcon icon={faSearch} /> Track by Number
              </button>
              <button
                className={`tracking-tab ${activeTab === "my-shipments" ? "active" : ""}`}
                onClick={() => setActiveTab("my-shipments")}
              >
                <FontAwesomeIcon icon={faBoxes} /> My Shipments
              </button>
            </div>
          )}

          {/* ============================================
              TRACK BY NUMBER
              ============================================ */}
          {activeTab === "track" && (
            <>
              <form onSubmit={handleTrack} className="tracking-form">
                <div className="tracking-input-group">
                  <div className="tracking-input-wrapper">
                    <FontAwesomeIcon icon={faSearch} className="tracking-input-icon" />
                    <input
                      type="text"
                      placeholder="Enter Tracking Number"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      className="tracking-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSearching}
                    className="tracking-submit-btn"
                  >
                    {isSearching ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Tracking...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSearch} /> Track Shipment
                      </>
                    )}
                  </Button>
                </div>
              </form>

              {error && (
                <motion.div
                  className="tracking-error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{error}</span>
                </motion.div>
              )}
            </>
          )}

          {/* ============================================
              MY SHIPMENTS
              ============================================ */}
          {activeTab === "my-shipments" && (
            <div className="my-shipments-section">
              <h3>
                <FontAwesomeIcon icon={faBoxes} /> Your Active Shipments
              </h3>
              {myShipments.length === 0 ? (
                <div className="my-shipments-empty">
                  <div className="my-shipments-empty__icon">📦</div>
                  <p>You don't have any active shipments.</p>
                  <p className="my-shipments-empty__sub">
                    When you place an order with Kenali Enterprise, you can track it here.
                  </p>
                  <div className="my-shipments-empty__actions">
                    <Button href="/products" variant="primary">
                      Start Shopping
                    </Button>
                    <a
                      href="https://wa.me/23231030808"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whatsapp-support"
                    >
                      <FontAwesomeIcon icon={faWhatsapp} /> Contact Support
                    </a>
                  </div>
                </div>
              ) : (
                <div className="my-shipments-list">
                  {myShipments.map((ship) => {
                    const malaysia = isFromMalaysia(ship);
                    return (
                      <div
                        key={ship._id}
                        className={`my-shipment-item ${malaysia ? "malaysia" : ""}`}
                        onClick={() => handleViewShipment(ship)}
                      >
                        <div>
                          <div className="my-shipment-item__tracking">
                            {ship.trackingNumber}
                            {malaysia && (
                              <span className="my-shipment-item__origin">
                                <FontAwesomeIcon icon={faGlobe} /> MY
                              </span>
                            )}
                            {ship.orderNumber && (
                              <span className="my-shipment-item__order">
                                Order: {ship.orderNumber}
                              </span>
                            )}
                          </div>
                          <div className="my-shipment-item__location">
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                            {ship.currentLocation || "Processing"}
                            {ship.origin?.country && (
                              <span>
                                <FontAwesomeIcon icon={faGlobe} /> {ship.origin.country}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="my-shipment-item__right">
                          <span
                            className="my-shipment-item__status"
                            style={{ background: getStatusColor(ship.status) }}
                          >
                            {getStatusLabel(ship.status)}
                          </span>
                          <span className="my-shipment-item__track">
                            Track →
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ============================================
              SHIPMENT DETAILS
              ============================================ */}
          {loading && !shipment && <Loader />}

          {shipment && (
            <motion.div
              className="shipment-details"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Header */}
              <div className="shipment-details__header">
                <div>
                  <div className="shipment-details__tracking-label">
                    Tracking Number
                  </div>
                  <div className="shipment-details__tracking-number">
                    {shipment.trackingNumber}
                    {isFromMalaysia(shipment) && (
                      <span className="shipment-details__origin-badge">
                        <FontAwesomeIcon icon={faGlobe} /> MY
                      </span>
                    )}
                  </div>
                  {shipment.supplier?.name && (
                    <div className="shipment-details__supplier">
                      <FontAwesomeIcon icon={faBuilding} /> Supplier: {shipment.supplier.name}
                      {shipment.supplier.country && ` (${shipment.supplier.country})`}
                    </div>
                  )}
                </div>
                <div
                  className="shipment-details__status-badge"
                  style={{ background: getStatusColor(shipment.status) }}
                >
                  <FontAwesomeIcon icon={getStatusIcon(shipment.status)} />
                  {getStatusLabel(shipment.status)}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="shipment-details__progress">
                <div className="shipment-details__progress-bar">
                  <div
                    className="shipment-details__progress-fill"
                    style={{ width: `${getProgressPercentage(shipment.status)}%` }}
                  />
                </div>
                <div className="shipment-details__progress-labels">
                  <span>Ordered</span>
                  <span>Processing</span>
                  <span>Shipped</span>
                  <span>In Transit</span>
                  <span>Customs</span>
                  <span>Delivered</span>
                </div>
              </div>

              {/* Info Grid */}
              <div className="shipment-details__grid">
                <div className="shipment-details__info">
                  <div className="shipment-details__info-label">Current Location</div>
                  <div className="shipment-details__info-value">
                    {shipment.currentLocation || "Processing"}
                  </div>
                </div>
                <div className="shipment-details__info">
                  <div className="shipment-details__info-label">Estimated Delivery</div>
                  <div className="shipment-details__info-value">
                    {shipment.estimatedDelivery
                      ? new Date(shipment.estimatedDelivery).toLocaleDateString()
                      : "To be confirmed"}
                  </div>
                </div>
                <div className="shipment-details__info">
                  <div className="shipment-details__info-label">Origin</div>
                  <div className="shipment-details__info-value">
                    {shipment.origin?.city || SUPPLIER.city},{" "}
                    {shipment.origin?.country || SUPPLIER.country}
                    {shipment.origin?.country === SUPPLIER.country && (
                      <span className="shipment-details__flag">🇲🇾</span>
                    )}
                  </div>
                </div>
                <div className="shipment-details__info">
                  <div className="shipment-details__info-label">Destination</div>
                  <div className="shipment-details__info-value">
                    {shipment.destination?.city || "Freetown"},{" "}
                    {shipment.destination?.country || "Sierra Leone"}
                    <span className="shipment-details__flag">🇸🇱</span>
                  </div>
                </div>
              </div>

              {/* Supplier Info */}
              <div className="shipment-details__supplier-info">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  This shipment is from <strong>{shipment.supplier?.name || SUPPLIER.name}</strong> in{" "}
                  <strong>{shipment.supplier?.country || SUPPLIER.country}</strong>.
                  {shipment.bulkDetails?.portOfLoading && (
                    <span> Loaded at {shipment.bulkDetails.portOfLoading}.</span>
                  )}
                  {shipment.bulkDetails?.portOfDischarge && (
                    <span> Discharging at {shipment.bulkDetails.portOfDischarge}.</span>
                  )}
                </span>
              </div>

              {/* Timeline */}
              <div className="shipment-details__timeline">
                <h4>Shipment Timeline</h4>
                <div className="shipment-details__timeline-track">
                  <div className="shipment-details__timeline-line" />
                  {shipment.timeline?.map((event, idx) => (
                    <div key={idx} className="shipment-details__timeline-event">
                      <div
                        className="shipment-details__timeline-dot"
                        style={{
                          background:
                            idx === shipment.timeline.length - 1
                              ? "#D9A321"
                              : getStatusColor(event.status),
                        }}
                      />
                      <div>
                        <div className="shipment-details__timeline-date">
                          {new Date(event.timestamp).toLocaleString()}
                        </div>
                        <div className="shipment-details__timeline-status">
                          {getStatusLabel(event.status)}
                        </div>
                        {event.location && (
                          <div className="shipment-details__timeline-location">
                            <FontAwesomeIcon icon={faMapMarkerAlt} /> {event.location}
                          </div>
                        )}
                        {event.description && (
                          <div className="shipment-details__timeline-description">
                            {event.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Help Section */}
              <div className="shipment-details__help">
                <h4>Need Help?</h4>
                <p>
                  If you have any questions about your shipment from {SUPPLIER.name}, please contact our support team:
                </p>
                <div className="shipment-details__help-links">
                  <a href="mailto:kenalienterprisesl@gmail.com">
                    <FontAwesomeIcon icon={faEnvelope} /> kenalienterprisesl@gmail.com
                  </a>
                  <a href="tel:+23231030808">
                    <FontAwesomeIcon icon={faPhone} /> +232 31 030808
                  </a>
                  <a href="https://wa.me/23231030808" target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        /* ========== HERO ========== */
        .tracking-hero {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          color: #FFFFFF;
          padding: 3rem 1.5rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .tracking-hero__bg {
          position: absolute;
          top: -50%;
          right: -20%;
          width: 500px;
          height: 500px;
          background: rgba(217, 163, 33, 0.05);
          border-radius: 50%;
        }

        .tracking-hero__content {
          position: relative;
          z-index: 2;
        }

        .tracking-hero__icon {
          font-size: 2.5rem;
          color: #D9A321;
          margin-bottom: 1rem;
        }

        .tracking-hero h1 {
          font-size: clamp(2rem, 4vw, 2.5rem);
          margin-bottom: 0.5rem;
        }

        .tracking-hero p {
          font-size: clamp(0.875rem, 1.2vw, 1rem);
          max-width: 600px;
          margin: 0 auto;
          opacity: 0.9;
        }

        .tracking-hero p strong {
          color: #D9A321;
        }

        .tracking-hero__info {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          flex-wrap: wrap;
          margin-top: 0.75rem;
          font-size: 0.75rem;
          opacity: 0.8;
        }

        .tracking-hero__info svg {
          margin-right: 0.25rem;
        }

        .tracking-hero__whatsapp {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #25d366;
          background: #ffffff;
          padding: 0.4rem 1rem;
          border-radius: 20px;
          font-size: 0.813rem;
          font-weight: 500;
          text-decoration: none;
          margin-top: 1rem;
          transition: all 0.3s ease;
        }

        .tracking-hero__whatsapp:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        @media (max-width: 480px) {
          .tracking-hero {
            padding: 2rem 1rem;
          }
          .tracking-hero__info {
            gap: 0.75rem;
            font-size: 0.65rem;
          }
        }

        /* ========== CONTENT ========== */
        .tracking-content {
          padding: 3rem 1.5rem;
          max-width: 900px;
          margin: 0 auto;
        }

        @media (max-width: 480px) {
          .tracking-content {
            padding: 2rem 0.75rem;
          }
        }

        .tracking-back {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #006B2E;
          text-decoration: none;
          margin-bottom: 2rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .tracking-back:hover {
          color: #D9A321;
          transform: translateX(-4px);
        }

        /* ========== INVENTORY NOTICE ========== */
        .tracking-inventory-notice {
          background: linear-gradient(135deg, #fef3c7, #fde68a);
          padding: 1rem 1.25rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          border: 2px solid #D9A321;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .tracking-inventory-notice__content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .tracking-inventory-notice__content svg {
          color: #d97706;
          font-size: 1.25rem;
        }

        .tracking-inventory-notice__content div {
          display: flex;
          flex-direction: column;
          color: #92400e;
          font-size: clamp(0.75rem, 1vw, 0.875rem);
        }

        .tracking-inventory-notice__content div strong {
          color: #78350f;
        }

        .tracking-inventory-notice__toggle {
          padding: 0.3rem 0.75rem;
          background: #D9A321;
          color: #ffffff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.75rem;
          font-family: "Inter", sans-serif;
          transition: all 0.3s ease;
        }

        .tracking-inventory-notice__toggle:hover {
          background: #B8860B;
        }

        @media (max-width: 480px) {
          .tracking-inventory-notice {
            flex-direction: column;
            text-align: center;
          }
          .tracking-inventory-notice__content {
            flex-direction: column;
          }
        }

        /* ========== INVENTORY LIST ========== */
        .tracking-inventory-list {
          background: #ffffff;
          padding: 1rem 1.25rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          border: 1px solid #e5e5e5;
          overflow: hidden;
        }

        .tracking-inventory-list h4 {
          color: #006B2E;
          margin-bottom: 0.75rem;
          font-size: 0.938rem;
        }

        .tracking-inventory-list h4 svg {
          margin-right: 0.5rem;
        }

        .tracking-inventory-item {
          padding: 0.75rem;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .tracking-inventory-item:last-child {
          border-bottom: none;
        }

        .tracking-inventory-item__tracking {
          font-weight: 600;
          color: #1F1F1F;
        }

        .tracking-inventory-item__badge {
          margin-left: 0.5rem;
          font-size: 0.6rem;
          background: #dcfce7;
          color: #16a34a;
          padding: 0.1rem 0.4rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
        }

        .tracking-inventory-item__route {
          font-size: 0.75rem;
          color: #6B7280;
        }

        .tracking-inventory-item__route svg {
          margin-right: 0.25rem;
        }

        .tracking-inventory-item__status {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .tracking-inventory-item__status-badge {
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          color: #FFF;
          font-size: 0.65rem;
          font-weight: 600;
        }

        .tracking-inventory-item__date {
          font-size: 0.7rem;
          color: #6B7280;
        }

        .tracking-inventory-list__note {
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #e5e5e5;
          font-size: 0.75rem;
          color: #6B7280;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .tracking-inventory-list__note svg {
          color: #D9A321;
        }

        /* ========== TABS ========== */
        .tracking-tabs-wrapper {
          margin-top: 1.5rem;
        }

        .tracking-tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .tracking-tab {
          padding: 0.6rem 1.5rem;
          border-radius: 8px;
          border: 2px solid transparent;
          background: transparent;
          color: #5A5A5A;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
          font-size: clamp(0.75rem, 0.9vw, 0.875rem);
          font-family: "Inter", sans-serif;
        }

        .tracking-tab:hover {
          border-color: #D9A321;
        }

        .tracking-tab.active {
          background: #D9A321;
          color: #ffffff;
          border-color: #D9A321;
          font-weight: 600;
        }

        .tracking-tab svg {
          margin-right: 0.4rem;
        }

        /* ========== TRACKING FORM ========== */
        .tracking-form {
          margin-bottom: 1.5rem;
        }

        .tracking-input-group {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .tracking-input-wrapper {
          position: relative;
          flex: 1;
          min-width: 200px;
        }

        .tracking-input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #6B7280;
        }

        .tracking-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.5rem;
          border: 2px solid #e5e5e5;
          border-radius: 8px;
          font-size: 1rem;
          outline: none;
          transition: border-color 0.3s ease;
          font-family: "Inter", sans-serif;
          background: #ffffff;
          color: #1F1F1F;
        }

        .tracking-input:focus {
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }

        .tracking-submit-btn {
          padding: 0.75rem 1.5rem;
          white-space: nowrap;
        }

        .tracking-error {
          background: #fee2e2;
          color: #dc2626;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-top: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        @media (max-width: 480px) {
          .tracking-input-group {
            flex-direction: column;
          }
          .tracking-submit-btn {
            width: 100%;
            justify-content: center;
          }
        }

        /* ========== MY SHIPMENTS ========== */
        .my-shipments-section {
          margin-top: 1.5rem;
        }

        .my-shipments-section h3 {
          color: #006B2E;
          margin-bottom: 1rem;
          font-size: 1.125rem;
        }

        .my-shipments-section h3 svg {
          margin-right: 0.5rem;
        }

        .my-shipments-empty {
          text-align: center;
          padding: 2rem;
          color: #6B7280;
        }

        .my-shipments-empty__icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .my-shipments-empty p {
          margin: 0;
        }

        .my-shipments-empty__sub {
          font-size: 0.875rem;
          margin-top: 0.5rem !important;
        }

        .my-shipments-empty__actions {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
          flex-wrap: wrap;
          margin-top: 1rem;
        }

        .whatsapp-support {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1.25rem;
          background: #25d366;
          color: #ffffff;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
        }

        .whatsapp-support:hover {
          background: #1da851;
          transform: translateY(-2px);
        }

        .my-shipments-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .my-shipment-item {
          background: #ffffff;
          padding: 1rem 1.25rem;
          border-radius: 12px;
          border: 1px solid #e5e5e5;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
          cursor: pointer;
          transition: all 0.3s ease;
          border-left: 4px solid transparent;
        }

        .my-shipment-item:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          transform: translateY(-2px);
        }

        .my-shipment-item.malaysia {
          border-left-color: #D9A321;
        }

        .my-shipment-item__tracking {
          font-weight: 600;
          color: #1F1F1F;
        }

        .my-shipment-item__origin {
          margin-left: 0.5rem;
          font-size: 0.6rem;
          background: #dcfce7;
          color: #16a34a;
          padding: 0.1rem 0.4rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
        }

        .my-shipment-item__order {
          font-size: 0.75rem;
          color: #6B7280;
          margin-left: 0.5rem;
        }

        .my-shipment-item__location {
          font-size: 0.813rem;
          color: #6B7280;
          margin-top: 0.15rem;
        }

        .my-shipment-item__location svg {
          margin-right: 0.25rem;
        }

        .my-shipment-item__location span {
          margin-left: 0.5rem;
        }

        .my-shipment-item__right {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .my-shipment-item__status {
          padding: 0.2rem 0.75rem;
          border-radius: 20px;
          color: #FFF;
          font-size: 0.65rem;
          font-weight: 600;
        }

        .my-shipment-item__track {
          color: #D9A321;
          font-weight: 500;
          font-size: 0.813rem;
        }

        /* ========== SHIPMENT DETAILS ========== */
        .shipment-details {
          margin-top: 2rem;
          background: #ffffff;
          border-radius: 16px;
          padding: 1.5rem;
          border: 1px solid #e5e5e5;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .shipment-details__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .shipment-details__tracking-label {
          font-size: 0.75rem;
          color: #6B7280;
        }

        .shipment-details__tracking-number {
          font-size: 1.25rem;
          font-weight: 700;
          color: #006B2E;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .shipment-details__origin-badge {
          font-size: 0.6rem;
          background: #dcfce7;
          color: #16a34a;
          padding: 0.1rem 0.4rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
        }

        .shipment-details__supplier {
          font-size: 0.7rem;
          color: #6B7280;
        }

        .shipment-details__supplier svg {
          margin-right: 0.25rem;
        }

        .shipment-details__status-badge {
          padding: 0.4rem 1rem;
          border-radius: 20px;
          color: #FFF;
          font-size: 0.875rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .shipment-details__progress {
          margin-bottom: 1.5rem;
        }

        .shipment-details__progress-bar {
          height: 6px;
          background: #e5e5e5;
          border-radius: 3px;
          overflow: hidden;
        }

        .shipment-details__progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #D9A321, #006B2E);
          border-radius: 3px;
          transition: width 0.5s ease;
        }

        .shipment-details__progress-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 0.5rem;
          font-size: 0.6rem;
          color: #6B7280;
          flex-wrap: wrap;
        }

        .shipment-details__grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .shipment-details__info {
          padding: 0.75rem;
          background: #F8F9FA;
          border-radius: 8px;
        }

        .shipment-details__info-label {
          font-size: 0.7rem;
          color: #6B7280;
        }

        .shipment-details__info-value {
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.3rem;
        }

        .shipment-details__flag {
          font-size: 0.8rem;
          margin-left: 0.2rem;
        }

        .shipment-details__supplier-info {
          background: #fef3c7;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          color: #92400e;
          flex-wrap: wrap;
        }

        .shipment-details__supplier-info svg {
          color: #D9A321;
        }

        .shipment-details__supplier-info strong {
          color: #78350f;
        }

        .shipment-details__timeline {
          margin-top: 1.5rem;
        }

        .shipment-details__timeline h4 {
          color: #006B2E;
          margin-bottom: 1rem;
        }

        .shipment-details__timeline-track {
          position: relative;
          padding-left: 1.5rem;
        }

        .shipment-details__timeline-line {
          position: absolute;
          left: 4px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: #e5e5e5;
        }

        .shipment-details__timeline-event {
          position: relative;
          padding-bottom: 1.5rem;
          padding-left: 1.5rem;
        }

        .shipment-details__timeline-dot {
          position: absolute;
          left: -1.25rem;
          top: 0.25rem;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid #ffffff;
          box-shadow: 0 0 0 2px #e5e5e5;
        }

        .shipment-details__timeline-date {
          font-size: 0.7rem;
          color: #6B7280;
        }

        .shipment-details__timeline-status {
          font-weight: 600;
          color: #1F1F1F;
          font-size: 0.875rem;
        }

        .shipment-details__timeline-location {
          font-size: 0.813rem;
          color: #6B7280;
        }

        .shipment-details__timeline-location svg {
          margin-right: 0.25rem;
        }

        .shipment-details__timeline-description {
          font-size: 0.813rem;
          color: #5A5A5A;
        }

        .shipment-details__help {
          margin-top: 1.5rem;
          padding: 1rem;
          background: #F8F9FA;
          border-radius: 8px;
          text-align: center;
        }

        .shipment-details__help h4 {
          color: #006B2E;
          margin-bottom: 0.5rem;
        }

        .shipment-details__help p {
          font-size: 0.875rem;
          color: #6B7280;
          margin-bottom: 0.75rem;
        }

        .shipment-details__help-links {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .shipment-details__help-links a {
          color: #006B2E;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.813rem;
          transition: color 0.3s ease;
        }

        .shipment-details__help-links a:hover {
          color: #D9A321;
        }

        .shipment-details__help-links a:last-child {
          color: #25d366;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .shipment-details {
            padding: 1.25rem;
          }
          .shipment-details__header {
            flex-direction: column;
            align-items: flex-start;
          }
          .shipment-details__grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 480px) {
          .shipment-details {
            padding: 1rem;
          }
          .shipment-details__grid {
            grid-template-columns: 1fr;
          }
          .shipment-details__help-links {
            flex-direction: column;
            align-items: center;
          }
          .shipment-details__tracking-number {
            font-size: 1rem;
            flex-wrap: wrap;
          }
          .shipment-details__progress-labels {
            gap: 0.25rem;
            justify-content: flex-start;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default OrderTracking;